import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { useProviderAuth } from '@/hooks/auth'
import { providerApiClient } from '@/lib/api'
import { providerProfileStepDataSchema } from '@/schemas/onboarding-provider-profile'
import type { ProviderProfileStepData } from '@/types/onboarding'
import { ProviderPreviewCard } from '../ProviderPreviewCard'

const STEP_INDEX = 5
const BIO_MAX = 500

function getStoredData(stepData: Record<string, unknown>): ProviderProfileStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = providerProfileStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

function defaultProfileData(): ProviderProfileStepData {
  return { bio: '', credentials_display: '', headshot_url: '' }
}

export function ProviderProfileStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()
  const { provider } = useProviderAuth()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const localPreviewRef = useRef<string | null>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)

  const [defaults] = useState<ProviderProfileStepData>(() => {
    const stored = getStoredData(state.stepData)
    return stored ?? defaultProfileData()
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ProviderProfileStepData>({
    resolver: zodResolver(providerProfileStepDataSchema),
    mode: 'onChange',
    defaultValues: defaults,
  })

  const bio = watch('bio')
  const credentialsDisplay = watch('credentials_display')
  const headshotUrl = watch('headshot_url')

  const uploadMutation = useMutation({
    mutationFn: (file: File) => providerApiClient.uploadProviderHeadshot(file),
    onSuccess: ({ url }) => {
      setValue('headshot_url', url, { shouldValidate: true, shouldDirty: true })
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current)
        localPreviewRef.current = null
      }
      setLocalPreview(null)
    },
    onError: () => {
      toast.error('Could not upload headshot. Please try again.')
    },
  })

  useEffect(() => {
    return () => {
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current)
      }
    }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const nextPreview = URL.createObjectURL(file)
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current)
    }
    localPreviewRef.current = nextPreview
    setLocalPreview(nextPreview)
    uploadMutation.mutate(file)
  }

  const onSubmit = (data: ProviderProfileStepData) => {
    setStepData(STEP_INDEX, data satisfies ProviderProfileStepData)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !isValid || uploadMutation.isPending,
    onClick: handleSubmit(onSubmit),
  })

  const previewImage = headshotUrl || localPreview

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Profile</h2>
        <p className="mt-1 text-sm text-slate-600">
          This is how you&apos;ll appear to patients on your booking page.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        aria-label="Provider profile form"
      >
        <div className="space-y-2">
          <Label htmlFor="provider-credentials-display">Credentials</Label>
          <Input
            id="provider-credentials-display"
            placeholder="Board-certified dermatologist"
            {...register('credentials_display')}
          />
          {errors.credentials_display && (
            <p className="text-sm text-red-600">
              {errors.credentials_display.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="provider-bio">Bio</Label>
          <Textarea
            id="provider-bio"
            rows={5}
            placeholder="Share a short introduction for patients."
            maxLength={BIO_MAX}
            {...register('bio')}
          />
          <p className="text-xs text-slate-500">
            {bio.length}/{BIO_MAX} characters
          </p>
          {errors.bio && <p className="text-sm text-red-600">{errors.bio.message}</p>}
        </div>

        <div className="space-y-2">
          <Label>Headshot</Label>
          <div className="flex items-start gap-4">
            {previewImage ? (
              <img
                src={previewImage}
                alt="Headshot preview"
                className="h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-400">
                No image
              </div>
            )}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                aria-label="Upload headshot"
                className="sr-only"
                onChange={handleFileSelect}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" aria-hidden />
                    {headshotUrl ? 'Replace photo' : 'Upload photo'}
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500">Square JPG or PNG, under 5 MB.</p>
            </div>
          </div>
          {errors.headshot_url && !localPreview && (
            <p className="text-sm text-red-600">{errors.headshot_url.message}</p>
          )}
        </div>
      </form>

      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700">
          Preview — how patients see you
        </p>
        <ProviderPreviewCard
          name={provider?.name ?? ''}
          credentialsDisplay={credentialsDisplay}
          bio={bio}
          avatarUrl={previewImage}
        />
      </div>
    </div>
  )
}
